import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { PageHeader } from '@/modules/common/components/PageHeader';
import { useLearningBudgetTransactionsDigest } from '@/modules/common/hooks/data-hooks/useLearningBudgetTransactionsDigest';
import { useEmployeeMutation } from '@/modules/common/hooks/useEmployer';
import { useUserUpdate } from '@/modules/common/hooks/useUser';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import {
  useAddTeamMember,
  useGetTeam,
  useGetTeams,
  useRemoveTeamMember,
} from '@/modules/employees/hooks/useTeams';
import { ViewEmployeePageSearchParams } from '@/routes/_admin/employees/$employeeId';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AccountBalanceWalletOutlined,
  ArrowBackIosOutlined,
  Edit,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { getRouteApi } from '@tanstack/react-router';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useGetEmployee } from '../hooks/useEmployees';
import { useLearningBudgetManagement } from '../hooks/useLearningBudgetManagement';

type Params = {
  employeeId: string;
};

type FormData = {
  firstName: string;
  lastName: string;
  jobRole: string;
  email: string;
  teamId?: string;
  isAdmin: boolean;
};

const routeApi = getRouteApi('/_admin/employees/$employeeId');

// TODO: add team member editing
export function ViewEmployeePage() {
  const { employeeId } = routeApi.useParams<Params>();
  const { employer, isLoading: isLoadingEmployer } =
    useCurrentUserContext() ?? {};
  const { useSearch } = routeApi;

  const { edit } = useSearch() as ViewEmployeePageSearchParams;

  const [isEditing, setIsEditing] = useState(!!edit);

  const {
    isLoading: isLoadingEmployee,
    data: { data: employee } = {},
    refetch: refetchEmployee,
  } = useGetEmployee(employer?._id ?? '', employeeId, {
    throwOnError: true,
    enabled: !!employer?._id,
  });

  const { isLoading: isLoadingTeam, data: { data: team } = {} } = useGetTeam(
    employee?.team as string,
    {
      enabled: !!employee?.team,
    },
  );
  const { isLoading: isLoadingTeams, data: { data: teams = [] } = {} } =
    useGetTeams(
      {
        page: 1,
        limit: 100000,
      },
      { enabled: !!employer?._id },
    );

  const {
    isLoading: isLoadingTransactions,
    isRefetching: isRefetchingTransactions,
    refetch: refetchTransactions,
    data: {
      data: {
        usedLearningBudget = 0,
        remainingLearningBudget = 0,
        totalLearningBudget = 0,
      } = {},
    } = {},
  } = useLearningBudgetTransactionsDigest({
    user: employeeId,
  });

  const { mutateAsync: updateUser } = useUserUpdate();
  const { mutateAsync: updateEmployee } = useEmployeeMutation();
  const { mutateAsync: addTeamMember } = useAddTeamMember();
  const { mutateAsync: removeMember } = useRemoveTeamMember(
    employee?.team?.toString() ?? '',
    employeeId,
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    values: {
      firstName: employee?.firstName ?? '',
      lastName: employee?.lastName ?? '',
      jobRole: employee?.jobRole ?? '',
      email: employee?.email ?? '',
      teamId: employee?.team?.toString() ?? '',
      isAdmin: employee?.isAdmin ?? false,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup.string().required(),
        lastName: yup.string().required(),
        jobRole: yup.string().required(),
        email: yup.string().email().required(),
        teamId: yup.string(),
        isAdmin: yup.boolean().required(),
      }),
    ),
  });

  const { Modal: LearningBudgetAllocationModal, showModal } =
    useLearningBudgetManagement({
      afterUpdate: refetchTransactions,
    });

  if (
    isLoadingEmployer ||
    isLoadingEmployee ||
    isLoadingTeam ||
    isLoadingTransactions ||
    isRefetchingTransactions ||
    isLoadingTeams
  ) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircularProgress />
      </div>
    );
  }

  if (!employee) {
    throw new Error('Employee not set');
  }

  const remainingPercentage =
    (remainingLearningBudget / totalLearningBudget) * 100;
  const currentTeamId = watch('teamId');

  const onSubmit = async (data: FormData) => {
    try {
      if (!employer?._id) {
        return;
      }

      await updateUser({
        _id: employeeId,
        firstName: data.firstName,
        lastName: data.lastName,
        jobRole: data.jobRole,
      });

      await updateEmployee({
        id: employer?._id ?? '',
        employeeId: employeeId,
        updatedData: {
          isAdmin: data.isAdmin,
        },
      });

      const oldTeamId = employee?.team?.toString();
      const newTeamId = data.teamId;

      const hasChangedTeam = newTeamId !== oldTeamId;

      if (hasChangedTeam) {
        if (oldTeamId) {
          await removeMember({
            teamId: oldTeamId,
            userId: employeeId,
          });
        }

        if (newTeamId) {
          await addTeamMember({
            teamId: newTeamId,
            email: employee.email,
          });
        }
      }

      await refetchEmployee();
      setIsEditing(false);
    } catch {
      enqueueSnackbar(
        'Failed to update employee information. Please try again.',
        {
          variant: 'error',
        },
      );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        pageTitle={
          <div className="flex items-center gap-1">
            <IconButton
              type="button"
              aria-label="Back"
              onClick={() => window.history.back()}
              size="small"
            >
              <ArrowBackIosOutlined className="text-base" />
            </IconButton>
            <Typography variant="h1">
              {employee.firstName} {employee.lastName}
            </Typography>
          </div>
        }
      />
      <form
        className="flex flex-col gap-8 px-8 pt-6"
        onSubmit={e => {
          if (isEditing) {
            handleSubmit(onSubmit)(e);
          }
        }}
      >
        <div className="flex-1">
          <header>
            <div className="flex justify-between">
              <Typography variant="h2" className="font-semibold">
                Employee Information
              </Typography>

              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
                className={isEditing ? 'invisible' : 'visible'}
              >
                Edit Information
              </Button>
            </div>
            <Typography>
              Update the employee's information, assign their team, and adjust
              admin rights.
            </Typography>
          </header>
          <div className="flex flex-col gap-8 mt-6">
            <div className="flex gap-6 flex-wrap">
              <div className="min-w-40">
                {isEditing ? (
                  <Controller
                    control={control}
                    name="firstName"
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel htmlFor="firstName">Name</FormLabel>
                        <TextField
                          size="small"
                          id="firstName"
                          aria-label="Name"
                          variant="outlined"
                          hiddenLabel
                          {...field}
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                        />
                      </FormControl>
                    )}
                  />
                ) : (
                  <>
                    <Typography variant="body2" className="font-semibold">
                      Name
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="whitespace-break-spaces break-all"
                    >
                      {employee.firstName}
                    </Typography>
                  </>
                )}
              </div>

              <div className="min-w-40">
                {isEditing ? (
                  <Controller
                    control={control}
                    name="lastName"
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel htmlFor="lastName">Surname</FormLabel>
                        <TextField
                          size="small"
                          id="lastName"
                          aria-label="Surname"
                          variant="outlined"
                          hiddenLabel
                          {...field}
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                        />
                      </FormControl>
                    )}
                  />
                ) : (
                  <>
                    <Typography variant="body2" className="font-semibold">
                      Surname
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="whitespace-break-spaces break-all"
                    >
                      {employee.lastName}
                    </Typography>
                  </>
                )}
              </div>

              <div className="min-w-40">
                {isEditing ? (
                  <Controller
                    control={control}
                    name="jobRole"
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel htmlFor="jobRole">Job Role</FormLabel>
                        <TextField
                          size="small"
                          id="jobRole"
                          aria-label="Job Role"
                          variant="outlined"
                          hiddenLabel
                          {...field}
                          error={!!errors.jobRole}
                          helperText={errors.jobRole?.message}
                        />
                      </FormControl>
                    )}
                  />
                ) : (
                  <>
                    <Typography variant="body2" className="font-semibold">
                      Job Role
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="whitespace-break-spaces break-all"
                    >
                      {employee.jobRole}
                    </Typography>
                  </>
                )}
              </div>

              <div className="min-w-40">
                {isEditing ? (
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <FormControl disabled>
                        <FormLabel htmlFor="email">Email address</FormLabel>
                        <TextField
                          size="small"
                          id="email"
                          aria-label="Email address"
                          variant="outlined"
                          disabled
                          hiddenLabel
                          {...field}
                          error={!!errors.email}
                          helperText={errors.email?.message}
                        />
                      </FormControl>
                    )}
                  />
                ) : (
                  <>
                    <Typography variant="body2" className="font-semibold">
                      Email address
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="whitespace-break-spaces break-all"
                    >
                      {employee.email}
                    </Typography>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-6 flex-wrap">
              <div className="min-w-40">
                {isEditing ? (
                  <Controller
                    control={control}
                    name="teamId"
                    render={({ field }) => (
                      <FormControl error={!!errors.teamId}>
                        <FormLabel htmlFor="teamId">Team</FormLabel>
                        <Select
                          size="small"
                          id="teamId"
                          aria-label="Team"
                          variant="outlined"
                          displayEmpty
                          renderValue={value => {
                            return value ? (
                              teams.find(t => t._id === value)?.name ?? ''
                            ) : (
                              <Typography color="text.secondary">
                                (no team selected)
                              </Typography>
                            );
                          }}
                          {...field}
                        >
                          {currentTeamId && (
                            <MenuItem value={''}>
                              <Typography color="text.secondary">
                                Remove from{' '}
                                {teams.find(t => t._id === currentTeamId)
                                  ?.name ?? ''}
                              </Typography>
                            </MenuItem>
                          )}
                          {teams.length === 0 ? (
                            <MenuItem value="" disabled>
                              No teams available
                            </MenuItem>
                          ) : (
                            teams.map(t => (
                              <MenuItem key={t._id} value={t._id}>
                                {t.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        <FormHelperText>
                          {errors.teamId?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                ) : (
                  <>
                    <Typography variant="body2" className="font-semibold">
                      Team
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="whitespace-break-spaces break-all"
                    >
                      {team?.name ?? '(no team)'}
                    </Typography>
                  </>
                )}
              </div>

              <div className="min-w-40">
                {isEditing ? (
                  <Controller
                    control={control}
                    name="isAdmin"
                    render={({ field }) => (
                      <FormControl error={!!errors.isAdmin}>
                        <FormLabel htmlFor="isAdmin">
                          Assign Admin Role?
                        </FormLabel>
                        <Switch
                          id="isAdmin"
                          checked={!!field.value}
                          onChange={field.onChange}
                        />
                        <FormHelperText>
                          {errors.isAdmin?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                ) : (
                  <>
                    <Typography variant="body2" className="font-semibold">
                      Has Admin Role?
                    </Typography>
                    <Typography
                      color="text.secondary"
                      className="whitespace-break-spaces break-all"
                    >
                      {employee.isAdmin ? 'Yes' : 'No'}
                    </Typography>
                  </>
                )}
              </div>
            </div>
          </div>
          {isEditing && (
            <footer className="flex gap-2 mt-6">
              <Button type="submit" loading={isSubmitting}>
                Save
              </Button>
              <Button
                disabled={isSubmitting}
                variant="outlined"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            </footer>
          )}
        </div>
        <hr />
        <div className="flex-1">
          <header>
            <div className="flex justify-between">
              <Typography variant="h2" className="font-semibold">
                Learning Budget
              </Typography>
              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  href={`/learning-budgets/transactions?userId=${employeeId}`}
                >
                  View LB transactions
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalanceWalletOutlined />}
                  onClick={() =>
                    showModal({
                      variant: 'user',
                      user: employee,
                    })
                  }
                >
                  Manage LB funds
                </Button>
              </div>
            </div>
            <Typography>
              Manage Learning Budget funds and review employee transactions.
            </Typography>
          </header>
          <div className="flex flex-col gap-8 mt-6">
            <dl className="flex gap-6 flex-wrap">
              {[
                [
                  'Current Balance',
                  formatCurrencyValue(totalLearningBudget, 'EUR'),
                ],
                ['Budget Used', formatCurrencyValue(usedLearningBudget, 'EUR')],
                [
                  'Remaining',
                  <>
                    {formatCurrencyValue(remainingLearningBudget, 'EUR')}{' '}
                    {Number.isFinite(remainingPercentage) && (
                      <span className="text-sm text-gray-500">
                        ({remainingPercentage.toFixed(2)}%)
                      </span>
                    )}
                  </>,
                ],
              ].map(([label, value]) => (
                <div key={label.toString()} className="min-w-52">
                  <Typography
                    variant="body2"
                    component={'dt'}
                    className="font-semibold"
                  >
                    {label}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    component={'dd'}
                    className="whitespace-break-spaces break-all"
                  >
                    {value}
                  </Typography>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <hr />
        <Alert
          title="Delete employee"
          icon={false}
          severity="error"
          className="rounded-xl border p-4"
          action={
            <Button variant="contained" color="error" disabled>
              Delete member
            </Button>
          }
        >
          <AlertTitle>Delete employee</AlertTitle>
          Deleting a member will remove their access and associated data.
        </Alert>
      </form>
      <LearningBudgetAllocationModal />
    </div>
  );
}
